import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { Box, IconButton, Stack, TableSortLabel } from '@mui/material';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { Row, RowData, TableOptions } from '@tanstack/react-table';

import { ChevronDownIcon, ChevronSelectorVerticalIcon, ChevronUpIcon } from '../icons';
import { Pagination } from '../pagination';

import {
  TableGroupingCardsGridStyled,
  TableGroupingCellStyled,
  TableGroupingCheckboxStyled,
  TableGroupingEmptyStyled,
  TableGroupingExpandCellStyled,
  TableGroupingExpandHeaderCellStyled,
  TableGroupingGroupContainerStyled,
  TableGroupingGroupHeaderStyled,
  TableGroupingHeaderCellStyled,
  TableGroupingHeaderGridStyled,
  TableGroupingOuterContainerStyled,
  TableGroupingRowExpandStyled,
  TableGroupingRowStyled,
} from './table-grouping.styled';

type TableGroupingProps<TData extends RowData> = Omit<
  TableOptions<TData>,
  | 'getCoreRowModel'
  | 'getGroupedRowModel'
  | 'getExpandedRowModel'
  | 'getSortedRowModel'
  | 'getPaginationRowModel'
> & {
  getGroupLabel?: (groupValue: unknown, index: number, groupRow: Row<TData>) => ReactNode;
  enableRowActions?: boolean;
  renderRowActionMenuItems?: (row: Row<TData>) => ReactNode;
  renderRowExpandComponent?: (row: Row<TData>) => ReactNode;
  emptyState?: ReactNode;
  enablePagination?: boolean;
  onRowClick?: (row: Row<TData>) => void;
};

export function TableGrouping<TData extends RowData>(props: TableGroupingProps<TData>) {
  const {
    getGroupLabel,
    enableRowActions,
    renderRowActionMenuItems,
    renderRowExpandComponent,
    emptyState,
    enablePagination,
    onRowClick,
    ...tableOptions
  } = props;

  const [expandedDetailRowIds, setExpandedDetailRowIds] = useState<Set<string>>(
    new Set(),
  );

  const toggleDetailRow = (rowId: string) => {
    setExpandedDetailRowIds((previous) => {
      const next = new Set(previous);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    groupedColumnMode: false,
    defaultColumn: { enableSorting: false },
    manualSorting: true,
    manualPagination: true,
    ...tableOptions,
    initialState: { expanded: true, ...tableOptions.initialState },
  });

  const hasSelection = props.enableRowSelection === true;
  const hasRowActions = Boolean(enableRowActions && renderRowActionMenuItems);
  const hasRowExpand = Boolean(renderRowExpandComponent);

  const gridTemplateColumns = [
    hasSelection && '40px',
    ...table
      .getVisibleLeafColumns()
      .map((column) => `minmax(${column.getSize()}px, 1fr)`),
    hasRowActions && '40px',
    hasRowExpand && '40px',
  ]
    .filter(Boolean)
    .join(' ');

  const groupedRows = table.getGroupedRowModel().rows;

  const pageIndex = table.getState().pagination.pageIndex;
  const pageCount = table.getPageCount();

  useEffect(() => {
    if (!enablePagination || pageIndex === 0 || pageIndex < pageCount) return;

    table.setPageIndex(Math.max(pageCount - 1, 0));
  }, [enablePagination, pageIndex, pageCount, table]);

  const renderDataRow = (row: Row<TData>) => {
    const isDetailExpanded = expandedDetailRowIds.has(row.id);

    return (
      <TableGroupingRowStyled
        key={row.id}
        $isSelected={row.getIsSelected()}
        sx={{ cursor: onRowClick ? 'pointer' : undefined }}
      >
        {hasSelection && (
          <TableGroupingCellStyled sx={{ justifyContent: 'center' }}>
            <TableGroupingCheckboxStyled
              size="small"
              checked={row.getIsSelected()}
              disabled={!row.getCanSelect()}
              onChange={row.getToggleSelectedHandler()}
            />
          </TableGroupingCellStyled>
        )}
        {row.getVisibleCells().map((cell) => (
          <TableGroupingCellStyled onClick={() => onRowClick?.(row)} key={cell.id}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableGroupingCellStyled>
        ))}
        {hasRowActions && (
          <TableGroupingExpandCellStyled $right={hasRowExpand ? 40 : 30}>
            {renderRowActionMenuItems?.(row)}
          </TableGroupingExpandCellStyled>
        )}
        {hasRowExpand && (
          <TableGroupingExpandCellStyled>
            <IconButton size="small" onClick={() => toggleDetailRow(row.id)}>
              {isDetailExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </IconButton>
          </TableGroupingExpandCellStyled>
        )}
        {hasRowExpand && isDetailExpanded && (
          <TableGroupingRowExpandStyled>
            {renderRowExpandComponent?.(row)}
          </TableGroupingRowExpandStyled>
        )}
      </TableGroupingRowStyled>
    );
  };

  return (
    <>
      <TableGroupingOuterContainerStyled>
        <TableGroupingHeaderGridStyled $gridTemplateColumns={gridTemplateColumns}>
          {hasSelection && (
            <TableGroupingHeaderCellStyled>
              <Box display="flex" alignItems="center" justifyContent="center">
                <TableGroupingCheckboxStyled
                  size="small"
                  checked={table.getIsAllPageRowsSelected()}
                  indeterminate={
                    table.getIsSomePageRowsSelected() && !table.getIsAllPageRowsSelected()
                  }
                  onChange={table.getToggleAllPageRowsSelectedHandler()}
                />
              </Box>
            </TableGroupingHeaderCellStyled>
          )}
          {table
            .getHeaderGroups()
            .flatMap((headerGroup) => headerGroup.headers)
            .map((header) => (
              <TableGroupingHeaderCellStyled key={header.id}>
                {header.column.getCanSort() ? (
                  <TableSortLabel
                    active={!!header.column.getIsSorted()}
                    direction={header.column.getIsSorted() || 'asc'}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getIsSorted() === 'asc' ? (
                      <ChevronUpIcon />
                    ) : header.column.getIsSorted() === 'desc' ? (
                      <ChevronDownIcon />
                    ) : (
                      <ChevronSelectorVerticalIcon />
                    )}
                  </TableSortLabel>
                ) : (
                  flexRender(header.column.columnDef.header, header.getContext())
                )}
              </TableGroupingHeaderCellStyled>
            ))}
          {hasRowActions && (
            <TableGroupingExpandHeaderCellStyled $right={hasRowExpand ? 40 : 0} />
          )}
          {hasRowExpand && <TableGroupingExpandHeaderCellStyled />}
        </TableGroupingHeaderGridStyled>

        {groupedRows.length === 0 ? (
          <TableGroupingEmptyStyled>{emptyState}</TableGroupingEmptyStyled>
        ) : (
          groupedRows.map((groupRow, groupIndex) => {
            // Rows whose grouping value is empty (e.g. a contract with no framework
            // number) are not a real group — render them flat, with no collapsible
            // header, always visible. Read the original cell value of the first row
            // rather than `groupRow.groupingValue`: the latter is the stringified map
            // key (`"null"`), so an empty value would otherwise form a "null" group.
            const groupingColumnId = groupRow.groupingColumnId;
            const rawGroupingValue = groupingColumnId
              ? groupRow.subRows[0]?.getValue(groupingColumnId)
              : undefined;
            const isUngrouped =
              rawGroupingValue === null ||
              rawGroupingValue === undefined ||
              rawGroupingValue === '';

            if (isUngrouped) {
              return (
                <TableGroupingGroupContainerStyled key={groupRow.id}>
                  <TableGroupingCardsGridStyled
                    $flat
                    $gridTemplateColumns={gridTemplateColumns}
                  >
                    {groupRow.subRows.map(renderDataRow)}
                  </TableGroupingCardsGridStyled>
                </TableGroupingGroupContainerStyled>
              );
            }

            const isGroupExpanded = groupRow.getIsExpanded();
            const groupLabel = getGroupLabel
              ? getGroupLabel(groupRow.groupingValue, groupIndex, groupRow)
              : String(groupRow.groupingValue);

            const isGroupAllSelected = groupRow.getIsAllSubRowsSelected();
            const isGroupSomeSelected = groupRow.getIsSomeSelected();

            return (
              <TableGroupingGroupContainerStyled key={groupRow.id}>
                <TableGroupingGroupHeaderStyled
                  onClick={groupRow.getToggleExpandedHandler()}
                  $isExpanded={isGroupExpanded}
                >
                  <Stack direction="row" alignItems="center" gap={1}>
                    {hasSelection && (
                      <TableGroupingCheckboxStyled
                        size="small"
                        checked={isGroupAllSelected}
                        indeterminate={isGroupSomeSelected && !isGroupAllSelected}
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                        onChange={groupRow.getToggleSelectedHandler()}
                      />
                    )}
                    {groupLabel}
                  </Stack>
                  {isGroupExpanded ? <ChevronDownIcon /> : <ChevronUpIcon />}
                </TableGroupingGroupHeaderStyled>

                {isGroupExpanded && (
                  <TableGroupingCardsGridStyled
                    $gridTemplateColumns={gridTemplateColumns}
                  >
                    {groupRow.subRows.map(renderDataRow)}
                  </TableGroupingCardsGridStyled>
                )}
              </TableGroupingGroupContainerStyled>
            );
          })
        )}
      </TableGroupingOuterContainerStyled>

      {enablePagination && (
        <Pagination
          count={Math.ceil(table.getRowCount() / table.getState().pagination.pageSize)}
          page={table.getState().pagination.pageIndex + 1}
          onPageChange={(page) => table.setPageIndex(page - 1)}
          canPreviousPage={table.getCanPreviousPage()}
          canNextPage={table.getCanNextPage()}
        />
      )}
    </>
  );
}
